import React from 'react';

interface Tag {
  id: string;
  name: string;
  description?: string;
  color: string;
  confidence_score?: number;
  created_by?: string;
  reasoning?: string; // AI-generated explanation for why this tag was assigned
}

interface TagDisplayProps {
  tags: Tag[];
  size?: 'sm' | 'md' | 'lg';
  showConfidence?: boolean;
  maxTags?: number;
  className?: string;
}

const TagDisplay: React.FC<TagDisplayProps> = ({ 
  tags, 
  size = 'sm', 
  showConfidence = false, 
  maxTags,
  className = ''
}) => {
  const displayTags = maxTags ? tags.slice(0, maxTags) : tags;
  const remainingCount = maxTags && tags.length > maxTags ? tags.length - maxTags : 0;

  const getSizeClasses = () => {
    switch (size) {
      case 'lg':
        return 'px-3 py-1.5 text-sm';
      case 'md':
        return 'px-2.5 py-1 text-xs';
      case 'sm':
      default:
        return 'px-2 py-0.5 text-xs';
    }
  };

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return '';
    if (confidence >= 0.8) return 'ring-2 ring-green-200';
    if (confidence >= 0.6) return 'ring-2 ring-yellow-200';
    return 'ring-2 ring-gray-200';
  };

  const getConfidenceLevel = (confidence?: number): string => {
    if (!confidence) return 'uncertain';
    if (confidence >= 0.9) return 'very high';
    if (confidence >= 0.8) return 'high';
    if (confidence >= 0.7) return 'good';
    if (confidence >= 0.6) return 'moderate';
    return 'low';
  };

  const createTooltip = (tag: Tag): string => {
    const confidencePercent = tag.confidence_score ? Math.round(tag.confidence_score * 100) : 0;
    const confidenceLevel = getConfidenceLevel(tag.confidence_score);
    
    // Create meaningful tooltip content
    const parts = [];
    
    // Prioritize AI reasoning over generic descriptions
    if (tag.reasoning && tag.reasoning.trim()) {
      parts.push(tag.reasoning);
    } else if (tag.description && !tag.description.includes('AI-generated')) {
      parts.push(tag.description);
    } else {
      parts.push(`Content tagged as ${tag.name.toLowerCase()}`);
    }
    
    // Confidence with context
    if (tag.confidence_score) {
      parts.push(`Analysis confidence: ${confidenceLevel} (${confidencePercent}%)`);
    }
    
    return parts.join(' • ');
  };

  if (tags.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {displayTags.map((tag) => (
        <span
          key={tag.id}
          className={`
            inline-flex items-center rounded-full font-medium cursor-help
            ${getSizeClasses()}
            ${showConfidence ? getConfidenceColor(tag.confidence_score) : ''}
            hover:shadow-sm transition-all duration-200
          `}
          style={{ 
            backgroundColor: `${tag.color}20`, 
            color: tag.color,
            borderColor: `${tag.color}40`
          }}
          title={createTooltip(tag)}
        >
          {tag.name}
          {showConfidence && tag.confidence_score && (
            <span className="ml-1 opacity-75">
              {Math.round(tag.confidence_score * 100)}%
            </span>
          )}
        </span>
      ))}
      {remainingCount > 0 && (
        <span 
          className={`
            inline-flex items-center rounded-full bg-gray-100 text-gray-600 font-medium
            ${getSizeClasses()}
          `}
          title={`${remainingCount} more tags - ${tags.slice(maxTags).map(t => t.name).join(', ')}`}
        >
          +{remainingCount}
        </span>
      )}
    </div>
  );
};

export default TagDisplay; 